import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../i18n";
import Text from "@ui/Text";
import { useFaultEvents, type FaultEvent } from "../../../../../api/useFaultEvents";
import { DatePicker } from "@ui/DatePicker";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { color } from "@ui";
import formatDate from "@ui/i18n/formatDate";
import type { Project } from "../../../../../api/useProjects";
import { parseAbsolute, toCalendarDate, type DateValue } from "@internationalized/date";
import InputV2 from "@ui/Input/InputV2";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import useToasts from "@ui/Toast/useToasts";
import useInput from "@ui/lib/hooks/useInput";
import { usePost } from "../../../../../api/useCall";
import {
  generateFaultEventPayload,
  listEventBaseEventSources,
} from "diagnostics-domain/faultEvent";
import { findEventPeriodsOverlap } from "diagnostics-domain/event";
import { useNavigate } from "react-router";
import { useRevalidate } from "../../../../../api/useQuery";
import * as io from "io-ts";
import { short } from "diagnostics-lib/io";
import { isLeft } from "fp-ts/lib/Either";
import { toPath } from "../../../../../router/helpers/path";

interface ModalFormSplitEventProps {
  project: Project;
  event: FaultEvent;
  onRequestClose: () => void;
}

const PATH = "/projects/:projectId(uuid)/faultEvents/split" as const;
const REVALIDATE_PATH = "/projects/:projectId(uuid)/faultEvents" as const;

const ModalFormSplitEvent = ({ project, event, onRequestClose }: ModalFormSplitEventProps) => {
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const splitEventCall = usePost(PATH);
  const revalidate = useRevalidate();

  const startDateBoundary = React.useMemo(
    () => parseAbsolute(event.startsAt.toISOString(), project.timezone).add({ days: 1 }),
    [event.startsAt, project.timezone],
  );
  const endDateBoundary = React.useMemo(
    () => parseAbsolute(event.endsAt.toISOString(), project.timezone),
    [event.endsAt, project.timezone],
  );

  const analysisDates = React.useMemo(
    () =>
      [
        ...new Set(
          event.metadata.faults.flatMap(fault =>
            fault.statusByAnalysis.map(analysis => analysis.date),
          ),
        ),
      ].sort((a, b) => a - b),
    [event.metadata.faults],
  );
  const getSplitAnalysisDate = React.useCallback(
    (exclusiveSplitDatePicker: DateValue | null) => {
      if (exclusiveSplitDatePicker === null) return null;

      const exclusiveSplitDate = toCalendarDate(exclusiveSplitDatePicker)
        .toDate(project.timezone)
        .getTime();
      const nextEventStartAt = analysisDates.filter(date => date >= exclusiveSplitDate).at(0);
      const previousEventEndAt = analysisDates.filter(date => date < exclusiveSplitDate).at(-1);

      if (previousEventEndAt === undefined || nextEventStartAt === undefined) return null;

      return {
        previousEventEndAt,
        nextEventStartAt,
      };
    },
    [project.timezone, analysisDates],
  );

  const [exclusiveSplitDate, setSplitDate] = React.useState<DateValue | null>(null);
  const onDateValueChange = React.useCallback(
    (value: DateValue | null) => {
      let resValue = value;
      if (resValue === null) {
        setSplitDate(null);
        return;
      }

      if (resValue.compare(startDateBoundary) < 0) {
        resValue = startDateBoundary;
      }
      if (resValue.compare(endDateBoundary) > 0) {
        resValue = endDateBoundary;
      }
      setSplitDate(resValue);
    },
    [startDateBoundary, endDateBoundary],
  );

  const {
    defaultValue: defaultPreviousEventName,
    value: previousEventName,
    onChange: onPreviousEventNamChange,
    dirty: previousEventNameDirty,
  } = useInput(
    t("project.fault_event.modal.form.split_event.input.previous_event.default_name", event.name),
  );
  const errorPreviousEventName = React.useMemo(() => {
    if (previousEventNameDirty && previousEventName.trim().length === 0)
      return t("project.fault_event.modal.form.split_event.input.error.empty_field");

    if (isLeft(short(io.string).decode(previousEventName.trim()))) {
      return t(
        "project.fault_event.modal.form.input.fault.name.error.cannot_exceed_100_characters",
      );
    }
    return;
  }, [previousEventNameDirty, previousEventName]);

  const {
    defaultValue: defaultNextEventName,
    value: nextEventName,
    onChange: onNextEventNamChange,
    dirty: nextEventNameDirty,
  } = useInput(
    t("project.fault_event.modal.form.split_event.input.next_event.default_name", event.name),
  );
  const errorNextEventName = React.useMemo(() => {
    if (nextEventNameDirty && nextEventName.trim().length === 0)
      return t("project.fault_event.modal.form.split_event.input.error.empty_field");

    if (isLeft(short(io.string).decode(nextEventName.trim()))) {
      return t(
        "project.fault_event.modal.form.input.fault.name.error.cannot_exceed_100_characters",
      );
    }
    return;
  }, [nextEventNameDirty, nextEventName]);

  const dirty =
    exclusiveSplitDate !== null &&
    errorPreviousEventName === undefined &&
    errorNextEventName === undefined;

  const faultEvents = useFaultEvents(event.projectId);
  const eventBaseEventSources = listEventBaseEventSources(event, faultEvents.persistedEvents);
  const eventBaseEventSourcePeriodsOverlap =
    eventBaseEventSources === null ? null : findEventPeriodsOverlap(eventBaseEventSources);

  const handleSubmit = React.useCallback(async () => {
    if (exclusiveSplitDate === null) return false;

    const splitAnalysisDate = getSplitAnalysisDate(exclusiveSplitDate);
    if (splitAnalysisDate === null) return false;

    if (event.sourceFaultEventIds.length > 0 && eventBaseEventSourcePeriodsOverlap === null) {
      return false;
    }

    try {
      const results = await splitEventCall({
        routeParams: { projectId: project.id },
        body: {
          source: {
            event: generateFaultEventPayload({
              faultEvent: event,
            }),
            newName: previousEventName,
            newEndsAt: new Date(splitAnalysisDate.previousEventEndAt),
          },
          split: {
            name: nextEventName,
            startsAt: new Date(splitAnalysisDate.nextEventStartAt),
          },
        },
      });
      await revalidate(REVALIDATE_PATH);
      addToast("success", t("project.fault_event.modal.form.split_event.toast.success"));
      void navigate(
        toPath("/projects/:projectId/fault-events/:faultEventId/fault", {
          projectId: project.id,
          faultEventId: results.body.sourceFaultEvent.id,
        }),
        { replace: true },
      );
      onRequestClose();
      return true;
    } catch (error) {
      addToast("error", t("errors.backend_error"));
      return false;
    }
  }, [
    exclusiveSplitDate,
    getSplitAnalysisDate,
    eventBaseEventSourcePeriodsOverlap,
    splitEventCall,
    project.id,
    event,
    previousEventName,
    nextEventName,
    revalidate,
    addToast,
    navigate,
    onRequestClose,
  ]);

  const minSplitDate =
    event.sourceFaultEventIds.length > 0 && eventBaseEventSourcePeriodsOverlap
      ? eventBaseEventSourcePeriodsOverlap.startsAt.toISOString()
      : event.startsAt.toISOString();
  const datePickerMinSplitDate = parseAbsolute(minSplitDate, project.timezone);
  const maxSplitDate =
    event.sourceFaultEventIds.length > 0 && eventBaseEventSourcePeriodsOverlap
      ? eventBaseEventSourcePeriodsOverlap.endsAt.toISOString()
      : event.endsAt.toISOString();
  const datePickerMaxSplitDate = parseAbsolute(maxSplitDate, project.timezone);

  return (
    <ModalForm
      className={modalForm}
      onSubmit={dirty ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("project.fault_event.modal.form.split_event.button.name.ok")}
    >
      <Text>
        {t(
          "project.fault_event.modal.form.split_event.description",
          <Text type="body1SemiBold" as="span">
            {event.name}
          </Text>,
        )}
      </Text>

      <div>
        <DatePicker
          id="split-date"
          data-cy="split-date"
          className={datePicker}
          dateOptions={{
            shouldCloseOnSelect: true,
            granularity: "day",
            placeholderValue: parseAbsolute(event.startsAt.toISOString(), project.timezone).add({
              days: 1,
            }),
            minValue: datePickerMinSplitDate,
            maxValue: datePickerMaxSplitDate,
            isDateUnavailable: date => {
              if (event.startsAt && date.compare(datePickerMinSplitDate) < 0) return true;
              if (event.endsAt && date.compare(datePickerMaxSplitDate) > 0) return true;
              return false;
            },
            value: exclusiveSplitDate,
            onChange: onDateValueChange,
          }}
          disabled={
            formatDate(event.startsAt, "slash", project.timezone) ===
              formatDate(event.endsAt, "slash", project.timezone) ||
            (event.sourceFaultEventIds.length > 0 && eventBaseEventSourcePeriodsOverlap === null)
          }
          label={t("project.fault_event.modal.form.datePicker.split_date.label")}
        />
        <Text className={datePickerInfo} type="body2">
          {t(
            "project.fault_event.modal.form.datePicker.split_date.info",
            <Text type="body2SemiBold" as="span">
              {formatDate(startDateBoundary.toDate(), "slash", project.timezone)} -{" "}
              {formatDate(endDateBoundary.toDate(), "slash", project.timezone)}
            </Text>,
          )}
        </Text>
      </div>

      <InputV2
        className={input}
        type="text"
        label={t("project.fault_event.modal.form.split_event.input.previous_event.label")}
        hint={t("project.fault_event.modal.form.split_event.input.previous_event.hint")}
        placeholder={defaultPreviousEventName}
        defaultValue={defaultPreviousEventName}
        value={previousEventName}
        onChange={onPreviousEventNamChange}
        revertable={previousEventNameDirty}
        error={errorPreviousEventName}
      />

      <InputV2
        className={input}
        type="text"
        label={t("project.fault_event.modal.form.split_event.input.next_event.label")}
        hint={t("project.fault_event.modal.form.split_event.input.next_event.hint")}
        placeholder={defaultNextEventName}
        defaultValue={defaultNextEventName}
        value={nextEventName}
        onChange={onNextEventNamChange}
        revertable={nextEventNameDirty}
        error={errorNextEventName}
      />

      {exclusiveSplitDate !== null && (
        <AdditionnalMessage
          status="INFO"
          text={t("project.fault_event.modal.form.split_event.message.redirection")}
        />
      )}
    </ModalForm>
  );
};

export default ModalFormSplitEvent;

//#region CSS
const modalForm = css`
  margin: 12px 0;
`;
const datePicker = css`
  width: fit-content;
  min-width: 240px;
`;
const datePickerInfo = css`
  color: ${color.carbon800};
  margin-top: ${spacings.xs};
  & > * {
    color: ${color.carbon900};
  }
`;
const input = css`
  width: fit-content;
  min-width: 368px;
`;
//#endregion
